import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0404 from "../lesson-04/lesson";
import lesson0406 from "../lesson-06/lesson";
import lesson0418 from "../lesson-18/lesson";

const linkLesson0404 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0404.id);
const linkLesson0406 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0406.id);
const linkLesson0418 = getLinkForCourseLesson("e0db4477-1995-4380-9c35-2338dcf5e504", lesson0418.id);

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>In this Lesson, the modelling of closed-box systems is presented [1, 2]. The input data will be the Thiele & Small (T&S) parameters as well as the maximum ratings for linear displacement and electrical power. The main design parameter of a closed box is the volume of the cabinet, which increases the resonant frequency and the quality factors of the driver. This has a direct impact on the frequency response of the system, which will be discussed in detail.
<h3 class=sectionHead>Acoustic short-circuit</h3>
<p>
   When the diaphragm of a loudspeaker vibrates, the air at the front is compressed while it expands at the back for positive displacements, and vice versa for negative excursions. At low frequencies, these compression and expansion cycles are slow and the pressure increase on one side of the diaphragm tends to compensate for the pressure decrease on the other. In other words, the two pressure fields radiated by the two sides of the diaphragm are in phase opposition and almost completely cancel each other out (see <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-1.svg data-img-alt="Figure 1. Acoustic short circuit in unbaffled loudspeakers.">Figure 1</a>). This results in an important drop in the efficiency of the loudspeaker. This effect is called the acoustic short circuit. At higher frequencies, the diaphragm oscillates faster and the loudspeaker is more directional, so that the compensation phenomenon gradually disappears.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-1.svg alt="Figure 1. Acoustic short circuit in unbaffled loudspeakers."><figcaption class=caption>Figure 1. Acoustic short circuit in unbaffled loudspeakers.</figcaption></figure>
<p>To overcome this problem, any of the following solutions can be carried out:
<ul class=itemize1>
   <li class=itemize>separate front and rear waves by mounting the loudspeaker in a large baffle or wall,
   <li class=itemize>block the rear wave by mounting the loudspeaker in an enclosure,
   <li class=itemize>bringing the rear wave into phase with the front wave by means of an acoustical circuit.
</ul>
<p>These different solutions will be reviewed in this and subsequent Lessons.
<h3 class=sectionHead>Large baffle</h3>
<p>
   The ideal solution to separate the front and rear half-spaces is the infinite baffle. Even if this is not feasible in practice, it provides a simple analytical solution for modelling the radiation from a loudspeaker: the baffled planar piston. Moreover, we recall that this analytical development has a low frequency expression which can be put in the form of a series association of a resistor and an inductance (see <a href="${linkLesson0406}" target="_blank">Lesson 6</a>). This facilitates its use in equivalent electrical circuits.<br class=newline>As the infinite baffle is not feasible in practice, a baffle of large size relative to the wavelength is used instead. In this case, each side of the membrane can be considered to radiate without interfering with the other. The large baffle is widely used by loudspeaker manufacturers for their experiments, to measure the frequency response of their drivers for datasheets, to compare experimental results with model predictions, etc. To make sure that all manufacturers use the same baffle dimensions, the International Electrotechnical Commission (IEC) has published baffle specifications for different speaker sizes. An example is given in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-2.svg data-img-alt="Figure 2. Standard measurement baffle.">Figure 2</a> for speakers with a maximum diameter of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mn>0</mn>
   </math>
   cm. Note that the speaker is off-centre to avoid diffraction effects from the edges of the baffle occurring at the same frequencies.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-2.svg alt="Figure 2. Standard measurement baffle."><figcaption class=caption>Figure 2. Standard measurement baffle.</figcaption></figure>
<h3 class=sectionHead>Equivalent circuit for a baffled speaker</h3>
<p>
   Let’s go back to the behavioural equations of the electrodynamic speaker in a large baffle seen in <a href="${linkLesson0418}" target="_blank">Lesson 18</a>, and write them in the low frequency range, <em><span class=cmti-10>i.e.</span></em>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>U</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mi>I</mi>
                           <mo class=MathClass-bin>+</mo>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <mi>B</mi>
                           <mi>ℓ</mi>
                           <mi>I</mi>
                           <mo class=MathClass-rel>=</mo>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>V</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-bin>+</mo>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>F</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mn>2</mn>
                           <msub>
                              <mrow>
                                 <mi>S</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                                 <mi>r</mi>
                              </mrow>
                           </msub>
                           <msub>
                              <mrow>
                                 <mi>Q</mi>
                              </mrow>
                              <mrow>
                                 <mi>d</mi>
                              </mrow>
                           </msub>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>r</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>j</mi>
      <mi>ω</mi>
      <mfrac>
         <mrow>
            <mn>8</mn>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>a</mi>
         </mrow>
         <mrow>
            <mn>3</mn>
            <mi>π</mi>
            <msub>
               <mrow>
                  <mi>S</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>
   at low frequencies. These three coupled equations can be summarised in the equivalent electrical circuit shown in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-3.svg data-img-alt="Figure 3. Equivalent circuit for a speaker in a large baffle.">Figure 3</a>, each loop corresponding to one domain.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-3.svg alt="Figure 3. Equivalent circuit for a speaker in a large baffle."><figcaption class=caption>Figure 3. Equivalent circuit for a speaker in a large baffle.</figcaption></figure>
<p>
   It is possible to bring the left loop, corresponding to the electrical domain, into the mechanical domain yielding to the circuit plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-4.svg data-img-alt="Figure 4. Equivalent circuit for a speaker in a large baffle in the mechanical and acoustical domains.">Figure 4</a>. The force
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
   </math>
   corresponds to a sinusoidal voltage source brought into the mechanical domain by the coupling and is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>F</mi>
         </mrow>
         <mrow>
            <mi>g</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>B</mi>
            <mi>ℓ</mi>
            <mi>U</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac>
   </math>. The electrical resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   becomes the mechanical resistance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>B</mi>
            <msup>
               <mrow>
                  <mi>ℓ</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
         </mrow>
      </mfrac></math>.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-4.svg alt="Figure 4. Equivalent circuit for a speaker in a large baffle in the mechanical and acoustical domains."><figcaption class=caption>Figure 4. Equivalent circuit for a speaker in a large baffle in the mechanical and acoustical domains.</figcaption></figure>
<p>Finally, the left mechanical loop is brought into the acoustical domain giving the series circuit shown in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-5.svg data-img-alt="Figure 5. Equivalent circuit for a speaker in a large baffle in the acoustical domain.">Figure 5</a>. The expression of the two-port elements coming from the mechanical domain are given by
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mrow>
               <mo fence=true form=prefix>{</mo>
               <mrow>
                  <mtable align=axis style="" equalrows=false equalcolumns=false class=array>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>p</mi>
                              </mrow>
                              <mrow>
                                 <mi>g</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>F</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>g</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>S</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>d</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mi>B</mi>
                                 <mi>ℓ</mi>
                                 <mi>U</mi>
                              </mrow>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>S</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>d</mi>
                                    </mrow>
                                 </msub>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>e</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>R</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                                 <mi>e</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>m</mi>
                                       <mi>e</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msubsup>
                                    <mrow>
                                       <mi>S</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>d</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <mi>B</mi>
                                 <msup>
                                    <mrow>
                                       <mi>ℓ</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msup>
                              </mrow>
                              <mrow>
                                 <msubsup>
                                    <mrow>
                                       <mi>S</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>d</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                                 <msub>
                                    <mrow>
                                       <mi>R</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>e</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                     <mtr>
                        <mtd class=array columnalign=center>
                           <msub>
                              <mrow>
                                 <mi>Z</mi>
                              </mrow>
                              <mrow>
                                 <mi>a</mi>
                                 <mi>m</mi>
                              </mrow>
                           </msub>
                           <mo class=MathClass-rel>=</mo>
                           <mfrac>
                              <mrow>
                                 <msub>
                                    <mrow>
                                       <mi>Z</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>m</mi>
                                    </mrow>
                                 </msub>
                              </mrow>
                              <mrow>
                                 <msubsup>
                                    <mrow>
                                       <mi>S</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>d</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                              </mrow>
                           </mfrac>
                        </mtd>
                     </mtr>
                  </mtable>
               </mrow>
            </mrow>
         </math>
      <td class=eq-no>(2)
</table>
<figure class=figure>
   <p>
      <img src=${config.ASSETS_URL}/04/20/theory/figure-5.svg alt="Figure 5. Equivalent circuit for a speaker in a large baffle in the acoustical domain.">
      <figcaption class=caption>Figure 5. Equivalent circuit for a speaker in a large baffle in the acoustical domain.</figcaption>
</figure>
<p>This equivalent electrical circuit makes it possible to quickly model a loudspeaker mounted on a loudspeaker cabinet by modifying the front and/or rear radiation impedances depending on the type of system considered. Let’s start with the sealed enclosure in the next section.
<h3 class=sectionHead>Closed-box system</h3>
<p>
   Large baffles are not really suitable for most applications. As an alternative, it is possible to block the rear wave by mounting the loudspeaker on a sealed enclosure of volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   (see <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-6.svg data-img-alt="Figure 6. Closed-box system.">Figure 6</a>). Optionally, the cabinet can be filled with damping material, which will bring some benefits as discussed later on.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-6.svg alt="Figure 6. Closed-box system."><figcaption class=caption>Figure 6. Closed-box system.</figcaption></figure>
<p>To model the closed box system, let’s start with the equivalent circuit shown in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-5.svg data-img-alt="Figure 5. Equivalent circuit for a speaker in a large baffle in the acoustical domain.">Figure 5</a> and integrate the changes due to the enclosure:
<ul class=itemize1>
   <li class=itemize>
      at low frequencies, a sealed enclosure is equivalent to an acoustic compliance of value
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>V</mi>
                  </mrow>
                  <mrow>
                     <mi>b</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>
      (see <a href="${linkLesson0404}" target="_blank">Lesson 4</a>),
   <li class=itemize>
      the losses in the enclosure volume are taken into account by an acoustic resistance which is noted
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>. When an absorbent material is added,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      can be calculated with theoretical models like the ones developed by Delany and Bazley, Miki, Johnson-Champoux-Allard, or Biot. Another solution consists in fitting the
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>
      value on measured impedance curves with and without material. Losses due to the absorbent material are usually modelled introducing a new quality factor
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      another source of losses is the leakage due to imperfect sealing of the cabinet. This can be modelled by a resistance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>
      , and associated to the quality factor
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
      </math>. In closed boxes filled with damping material it is generally assumed that losses due to leakage are negligible and therefore, in the following we will consider
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
      </math>
      (this is to say, <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>→</mo>
         <mi>∞</mi>
      </math>),
   <li class=itemize>
      there may also be a significant change in the radiation conditions of the loudspeaker. This is the case, for example, if the T&S parameters were evaluated in a large cabinet and the loudspeaker was mounted on a very small closed cabinet. In this Lesson, we will assume that the front of the enclosure is of sufficient size that the front and rear radiation masses of the loudspeaker are very close to the baffled case and equal to
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>r</mi>
            </mrow>
         </msub>
      </math>. When this is not the case, formulations given in the scientific literature can approximate the radiation impedances as a function of the enclosure dimensions.
</ul>
<p>
   Using these assumptions, the electrical circuit equivalent to the sealed enclosure in the acoustical domain is given in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-7.svg data-img-alt="Figure 7. Equivalent circuit for a speaker in a sealed enclosure in the acoustical domain.">Figure 7</a>.<br class=newline>
   <figure class=figure>
      <img src=${config.ASSETS_URL}/04/20/theory/figure-7.svg alt="Figure 7. Equivalent circuit for a speaker in a sealed enclosure in the acoustical domain.">
      <figcaption class=caption>Figure 7. Equivalent circuit for a speaker in a sealed enclosure in the acoustical domain.</figcaption>
   </figure>
<p>It can be seen that compared to the baffled case, the type of circuit does not change, it is always an RLC circuit in series. However, the value of the elements changes:
<ul class=itemize1>
   <li class=itemize>
      the total capacitance
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>, resulting from the speaker and box compliances is given by:
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>a</mi>
                     <mi>b</mi>
                  </mrow>
               </msub>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>a</mi>
                     <mi>s</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>a</mi>
                     <mi>b</mi>
                  </mrow>
               </msub>
               <mo class=MathClass-bin>+</mo>
               <msub>
                  <mrow>
                     <mi>C</mi>
                  </mrow>
                  <mrow>
                     <mi>a</mi>
                     <mi>s</mi>
                  </mrow>
               </msub>
            </mrow>
         </mfrac>
      </math>,
   <li class=itemize>
      the total resistance term is given by
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>s</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>+</mo>
         <msub>
            <mrow>
               <mi>R</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>,
   <li class=itemize>
      using our radiation assumption, the mass term that remains unchanged from the baffled case is equal to 
      <span style="font-size: 12px">
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>s</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∕</mo>
         <msubsup>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <mo class=MathClass-bin>+</mo>
         <mn>2</mn>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>r</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msub>
            <mrow>
               <mi>M</mi>
            </mrow>
            <mrow>
               <mi>m</mi>
               <mi>s</mi>
            </mrow>
         </msub>
         <mo class=MathClass-bin>∕</mo>
         <msubsup>
            <mrow>
               <mi>S</mi>
            </mrow>
            <mrow>
               <mi>d</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
      </math></span>.
</ul>
<p>
   Let us introduce the parameter
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   which is the ratio of the compliance of the loudspeaker to that of the cabinet: <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo>
      <mi>α</mi>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-close>)</mo>
   </math>. Using
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>, the total capacitance
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>C</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   is given by
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>C</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>s</mi>
                     </mrow>
                  </msub>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>s</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-bin>+</mo>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </mfrac>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mi>α</mi>
                  <msub>
                     <mrow>
                        <mi>C</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   Let us now study the effect of the enclosure on the parameters governing the response of the system, starting with resonant frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   of the speaker mounted on the enclosure, which is given by
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msqrt>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <mi>α</mi>
                     </mrow>
                  </msqrt>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </msqrt>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mi>s</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   is positive, we have
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>></mo>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>. The addition of an enclosure therefore increases the resonance frequency of the system, the smaller the volume of the enclosure
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   the higher
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>. As for the speaker in a baffle, it is possible to define a total quality factor
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   of the sealed system depending on an electrical, mechanical and acoustical quality factors
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi>e</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi>m</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mn>1</mn>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   The expressions of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   can be extrapolated from the baffled case
<table class=equation>
   <tr>
      <td style="font-size: 6px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                     </mrow>
                  </msub>
                  <msubsup>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msubsup>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <msup>
                     <mrow>
                        <mi>ℓ</mi>
                     </mrow>
                     <mrow>
                        <mn>2</mn>
                     </mrow>
                  </msup>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>e</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </msqrt>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </msqrt>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
                  <mi>s</mi>
               </mrow>
            </msub>
         </math>
      <td class=eq-no>(6)
</table>
<table class=equation>
   <tr>
      <td style="font-size: 8px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msqrt>
                     <mrow>
                        <mn>1</mn>
                        <mo class=MathClass-bin>+</mo>
                        <mi>α</mi>
                     </mrow>
                  </msqrt>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>s</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>s</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
            <mo class=MathClass-rel>=</mo>
            <msqrt>
               <mrow>
                  <mn>1</mn>
                  <mo class=MathClass-bin>+</mo>
                  <mi>α</mi>
               </mrow>
            </msqrt>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>m</mi>
                  <mi>s</mi>
               </mrow>
            </msub>
          </math>
      <td class=eq-no>(7)
</table>
<p>and
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>R</mi>
                     </mrow>
                     <mrow>
                        <mi>a</mi>
                        <mi>b</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <msqrt>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>M</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>C</mi>
                           </mrow>
                           <mrow>
                              <mi>a</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
            </msqrt>
         </math>
      <td class=eq-no>(8)
</table>
<p>
   As
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   is difficult to estimate, it is usually assumed
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>5</mn>
   </math>
   in enclosures filled with damping material (otherwise
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mn>0</mn>
   </math>
   can be considered).<br class=newline>
<p>Similar to what has been calculated for the loudspeaker in a baffle, the following flow rate is obtained for the sealed enclosure
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>q</mi>
               </mrow>
               <mrow>
                  <mi>d</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi>U</mi>
               </mrow>
               <mrow>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi>j</mi>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(9)
</table>
<p>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ω</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>. And the on-axis pressure is then given by
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mi>a</mi>
                  <mi>x</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>S</mi>
                     </mrow>
                     <mrow>
                        <mi>d</mi>
                     </mrow>
                  </msub>
                  <mi>U</mi>
                  <mi>ω</mi>
                  <msub>
                     <mrow>
                        <mi>ρ</mi>
                     </mrow>
                     <mrow>
                        <mn>0</mn>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>B</mi>
                  <mi>ℓ</mi>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>e</mi>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </mrow>
            </mfrac>
            <mfrac>
               <mrow>
                  <mfrac>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
               </mrow>
               <mrow>
                  <mfrac>
                     <mrow>
                        <msup>
                           <mrow>
                              <mo class=MathClass-open>(</mo>
                              <mi>j</mi>
                              <mi>ω</mi>
                              <mo class=MathClass-close>)</mo>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msup>
                     </mrow>
                     <mrow>
                        <msubsup>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                           <mrow>
                              <mn>2</mn>
                           </mrow>
                        </msubsup>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mfrac>
                     <mrow>
                        <mi>j</mi>
                        <mi>ω</mi>
                     </mrow>
                     <mrow>
                        <msub>
                           <mrow>
                              <mi>Q</mi>
                           </mrow>
                           <mrow>
                              <mi>t</mi>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                        <msub>
                           <mrow>
                              <mi>ω</mi>
                           </mrow>
                           <mrow>
                              <mi>c</mi>
                           </mrow>
                        </msub>
                     </mrow>
                  </mfrac>
                  <mo class=MathClass-bin>+</mo>
                  <mn>1</mn>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(10)
</table>
<p>
   Using the above equation, the pressure level normalized to the plateau value on the axis is plotted in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-8.svg data-img-alt="Figure 8. On-axis normalised response of a driver for different acoustic loads.">Figure 8</a> for a typical woofer. This woofer has the following parameters:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo>
       <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>6</mn>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>m</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>3</mn>
       <mo>)</mo>
   </math> and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>5</mn>
      <mn>0</mn>
   </math>
   Hz. The response for three enclosure volumes with negligible losses
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mn>0</mn>
   </math>
   is compared to the baffled response. As expected, the values of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   increase as the volume
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   decreases. Thus, if one wants to obtain a target response with a
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
            <mi>c</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≃</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
   </math>
   (see green curve in <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-8.svg data-img-alt="Figure 8. On-axis normalised response of a driver for different acoustic loads.">Figure 8</a>), it is necessary to choose a speaker with a value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>Q</mi>
         </mrow>
         <mrow>
            <mi>t</mi>
            <mi>s</mi>
         </mrow>
      </msub>
   </math>
   lower than
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
   </math>. The cabinet, due to the stiffness of the air within it, will then increase the value of the total quality factor to obtain the target value (see green and blue curves). It is noticeable that if the volume is too small, the low cut-off frequency increases too much and a noticeable bass boost appears on the frequency response (see red curve for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>a</mi>
            <mi>s</mi>
         </mrow>
      </msub>
      <mo class=MathClass-bin>∕</mo>
      <mn>3</mn>
   </math>), which causes some ripple in the impulse response of the system.<br class=newline>
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-8.svg alt="Figure 8. On-axis normalised response of a driver for different acoustic loads."><figcaption class=caption>Figure 8. On-axis normalised response of a driver for different acoustic loads.</figcaption></figure>
<h3 class=sectionHead>Damping material filling</h3>
<p>In general, filling with absorbent material is highly recommended in sealed enclosures. This serves for different purposes:
<ul class=itemize1>
   <li class=itemize>absorbing standing waves at the resonance frequencies of the enclosure that could be re-emitted through the membrane,
   <li class=itemize>
      decreasing the speed of sound in the enclosure to increase its apparent volume. Filling the enclosure with an absorbent material, the expansion and compression of the air no longer take place adiabatically but rather isothermally, thanks to the high thermal capacity of the filling material, which limits temperature changes. The speed of sound is then
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <msqrt>
            <mrow>
               <mfrac>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>P</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mrow>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>ρ</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mrow>
               </mfrac>
            </mrow>
         </msqrt>
      </math>
      instead of
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>c</mi>
         <mo class=MathClass-rel>=</mo>
         <msqrt>
            <mrow>
               <mfrac>
                  <mrow>
                     <mi>γ</mi>
                     <msub>
                        <mrow>
                           <mi>P</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mrow>
                  <mrow>
                     <msub>
                        <mrow>
                           <mi>ρ</mi>
                        </mrow>
                        <mrow>
                           <mn>0</mn>
                        </mrow>
                     </msub>
                  </mrow>
               </mfrac>
            </mrow>
         </msqrt>
      </math>, so we have
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msubsup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mi>i</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msubsup>
         <mo class=MathClass-rel>=</mo>
         <msup>
            <mrow>
               <mi>c</mi>
            </mrow>
            <mrow>
               <mn>2</mn>
            </mrow>
         </msup>
         <mo class=MathClass-bin>∕</mo>
         <mi>γ</mi>
      </math>, where
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>γ</mi>
         <mo class=MathClass-rel>=</mo>
         <mn>1</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>4</mn>
      </math>
      is the the ratio of specific heats at constant pressure and at constant volume for air. The volume compliance is then
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>C</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <msub>
                  <mrow>
                     <mi>V</mi>
                  </mrow>
                  <mrow>
                     <mi>b</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <msubsup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mi>i</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msubsup>
            </mrow>
         </mfrac>
         <mo class=MathClass-rel>=</mo>
         <mfrac>
            <mrow>
               <mi>γ</mi>
               <msub>
                  <mrow>
                     <mi>V</mi>
                  </mrow>
                  <mrow>
                     <mi>b</mi>
                  </mrow>
               </msub>
            </mrow>
            <mrow>
               <msub>
                  <mrow>
                     <mi>ρ</mi>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
               </msub>
               <msup>
                  <mrow>
                     <mi>c</mi>
                  </mrow>
                  <mrow>
                     <mn>2</mn>
                  </mrow>
               </msup>
            </mrow>
         </mfrac>
      </math>, so it is as if the apparent volume of the enclosure were 40% larger (for a complete filling of
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>). In practice, the increase is more in the order of 20-25% as the process is not completely isothermal.
</ul>
<h3 class=sectionHead>Performance of sealed enclosures</h3>
<p>The performance of sealed enclosures can be assessed by means of the following properties:
<ul class=itemize1>
   <li class=itemize>
      <strong>
         <span class=cmbx-10>physical volume </span><math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mo>(</mo><msub>
               <mrow>
                  <mi>V</mi>
               </mrow>
               <mrow>
                  <mi>b</mi>
               </mrow>
            </msub>
          <mo>)</mo>
         </math><span class=cmbx-10>:</span>
      </strong>
      this parameter refers to the interior volume of the cabinet, which may be different to the apparent acoustical volume
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>. Notice that, with damping material, about 20% of the cabinet’s volume could be reduced to achieve the same
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>V</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
               <mi>b</mi>
            </mrow>
         </msub>
      </math>. Assessing performance in terms of volume, it is desirable to design enclosures as compact as possible,
   <li class=itemize>
      <strong><span class=cmbx-10>lower cutoff frequency:</span></strong> There is not a clear consensus about the criterion to determine the lower end. Based on filter theory, the cutoff frequency at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-bin>−</mo>
         <mn>3</mn>
      </math>
      dB 
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo>(</mo><msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msub>
          <mo>)</mo>
      </math> has been extensively employed. On the other hand, according to the international standard for loudspeaker IEC 60268-5, the frequency limits are defined at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-bin>−</mo>
         <mn>1</mn>
         <mn>0</mn>
      </math>
      dB (in this text we will refer it as
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>). This criterion mainly stands for specifications and labelling of commercial loudspeaker systems. Comparing both criteria,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>
      is significantly lower than
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msub>
      </math>. In between, the frequency at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo class=MathClass-bin>−</mo>
         <mn>6</mn>
      </math>
      dB <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mo>(</mo>
       <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>6</mn>
            </mrow>
         </msub>
       <mo>)</mo>
      </math> could also be employed to better represent the perceived lower limit. Independently of the criterion employed, the important question here is to use the same criterion to compare different designs. For
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>3</mn>
            </mrow>
         </msub>
      </math>,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>6</mn>
            </mrow>
         </msub>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>f</mi>
            </mrow>
            <mrow>
               <mn>1</mn>
               <mn>0</mn>
            </mrow>
         </msub>
      </math>, closed forms expressions can be derived
      <table class=equation>
         <tr>
            <td style="font-size: 9px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>3</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mo fence=true form=prefix>(</mo>
                        <mrow>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mn>2</mn>
                                 <msubsup>
                                    <mrow>
                                       <mi>Q</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                       <mi>c</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-bin>−</mo>
                           <mn>1</mn>
                           <mo class=MathClass-bin>+</mo>
                           <msqrt>
                              <mrow>
                                 <msup>
                                    <mrow>
                                       <mrow>
                                          <mo fence=true form=prefix>(</mo>
                                          <mrow>
                                             <mfrac>
                                                <mrow>
                                                   <mn>1</mn>
                                                </mrow>
                                                <mrow>
                                                   <mn>2</mn>
                                                   <msubsup>
                                                      <mrow>
                                                         <mi>Q</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mi>t</mi>
                                                         <mi>c</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mn>2</mn>
                                                      </mrow>
                                                   </msubsup>
                                                </mrow>
                                             </mfrac>
                                             <mo class=MathClass-bin>−</mo>
                                             <mn>1</mn>
                                          </mrow>
                                          <mo fence=true form=postfix>)</mo>
                                       </mrow>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msup>
                                 <mo class=MathClass-bin>+</mo>
                                 <mn>1</mn>
                              </mrow>
                           </msqrt>
                        </mrow>
                        <mo fence=true form=postfix>)</mo>
                     </mrow>
                     <mrow>
                        <mfrac>
                           <mn>1</mn>
                           <mn>2</mn>
                        </mfrac>
                     </mrow>
                  </msup>
               </math>
            <td class=eq-no>(11)
      </table>
      <table class=equation>
         <tr>
            <td style="font-size: 8px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>6</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mo fence=true form=prefix>(</mo>
                        <mrow>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mn>6</mn>
                                 <msubsup>
                                    <mrow>
                                       <mi>Q</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                       <mi>c</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mn>3</mn>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-bin>+</mo>
                           <msqrt>
                              <mrow>
                                 <msup>
                                    <mrow>
                                       <mrow>
                                          <mo fence=true form=prefix>(</mo>
                                          <mrow>
                                             <mfrac>
                                                <mrow>
                                                   <mn>1</mn>
                                                </mrow>
                                                <mrow>
                                                   <mn>6</mn>
                                                   <msubsup>
                                                      <mrow>
                                                         <mi>Q</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mi>t</mi>
                                                         <mi>c</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mn>2</mn>
                                                      </mrow>
                                                   </msubsup>
                                                </mrow>
                                             </mfrac>
                                             <mo class=MathClass-bin>−</mo>
                                             <mfrac>
                                                <mrow>
                                                   <mn>1</mn>
                                                </mrow>
                                                <mrow>
                                                   <mn>3</mn>
                                                </mrow>
                                             </mfrac>
                                          </mrow>
                                          <mo fence=true form=postfix>)</mo>
                                       </mrow>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msup>
                                 <mo class=MathClass-bin>+</mo>
                                 <mfrac>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                    <mrow>
                                       <mn>3</mn>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                           </msqrt>
                        </mrow>
                        <mo fence=true form=postfix>)</mo>
                     </mrow>
                     <mrow>
                        <mfrac>
                           <mn>1</mn>
                           <mn>2</mn>
                        </mfrac>
                     </mrow>
                  </msup>
               </math>
            <td class=eq-no>(12)
      </table>
      <p>and
      <table class=equation>
         <tr>
            <td style="font-size: 8px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mo class=MathClass-rel>=</mo>
                  <msub>
                     <mrow>
                        <mi>f</mi>
                     </mrow>
                     <mrow>
                        <mi>c</mi>
                     </mrow>
                  </msub>
                  <msup>
                     <mrow>
                        <mo fence=true form=prefix>(</mo>
                        <mrow>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mn>1</mn>
                                 <mn>8</mn>
                                 <msubsup>
                                    <mrow>
                                       <mi>Q</mi>
                                    </mrow>
                                    <mrow>
                                       <mi>t</mi>
                                       <mi>c</mi>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msubsup>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-bin>−</mo>
                           <mfrac>
                              <mrow>
                                 <mn>1</mn>
                              </mrow>
                              <mrow>
                                 <mn>9</mn>
                              </mrow>
                           </mfrac>
                           <mo class=MathClass-bin>+</mo>
                           <msqrt>
                              <mrow>
                                 <msup>
                                    <mrow>
                                       <mrow>
                                          <mo fence=true form=prefix>(</mo>
                                          <mrow>
                                             <mfrac>
                                                <mrow>
                                                   <mn>1</mn>
                                                </mrow>
                                                <mrow>
                                                   <mn>1</mn>
                                                   <mn>8</mn>
                                                   <msubsup>
                                                      <mrow>
                                                         <mi>Q</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mi>t</mi>
                                                         <mi>c</mi>
                                                      </mrow>
                                                      <mrow>
                                                         <mn>2</mn>
                                                      </mrow>
                                                   </msubsup>
                                                </mrow>
                                             </mfrac>
                                             <mo class=MathClass-bin>−</mo>
                                             <mfrac>
                                                <mrow>
                                                   <mn>1</mn>
                                                </mrow>
                                                <mrow>
                                                   <mn>9</mn>
                                                </mrow>
                                             </mfrac>
                                          </mrow>
                                          <mo fence=true form=postfix>)</mo>
                                       </mrow>
                                    </mrow>
                                    <mrow>
                                       <mn>2</mn>
                                    </mrow>
                                 </msup>
                                 <mo class=MathClass-bin>+</mo>
                                 <mfrac>
                                    <mrow>
                                       <mn>1</mn>
                                    </mrow>
                                    <mrow>
                                       <mn>9</mn>
                                    </mrow>
                                 </mfrac>
                              </mrow>
                           </msqrt>
                        </mrow>
                        <mo fence=true form=postfix>)</mo>
                     </mrow>
                     <mrow>
                        <mfrac>
                           <mn>1</mn>
                           <mn>2</mn>
                        </mfrac>
                     </mrow>
                  </msup>
               </math>
            <td class=eq-no>(13)
      </table>
   <li class=itemize>
      <strong><span class=cmbx-10>ripple:</span></strong> ripple refers for the difference —expressed in dB— between the peak and the bandpass response. For
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
               <mi>c</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>≤</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>7</mn>
         <mn>0</mn>
         <mn>7</mn>
      </math>
      the frequency response is always flat, so there is not ripple. Above that value, ripple can be computed from
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>t</mi>
               <mi>c</mi>
            </mrow>
         </msub>
      </math>
      <table class=equation>
         <tr>
            <td style="font-size: 9px">
               <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
                  <mstyle mathvariant=normal>
                     <mi>r</mi>
                     <mi>i</mi>
                     <mi>p</mi>
                     <mi>p</mi>
                     <mi>l</mi>
                     <mi>e</mi>
                  </mstyle>
                  <mo class=MathClass-rel>=</mo>
                  <mn>2</mn>
                  <mn>0</mn>
                  <msub>
                     <mrow>
                        <mi class=qopname>log</mi>
                        <mo>⁡</mo>
                     </mrow>
                     <mrow>
                        <mn>1</mn>
                        <mn>0</mn>
                     </mrow>
                  </msub>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mfrac>
                           <mrow>
                              <msubsup>
                                 <mrow>
                                    <mi>Q</mi>
                                 </mrow>
                                 <mrow>
                                    <mi>t</mi>
                                    <mi>c</mi>
                                 </mrow>
                                 <mrow>
                                    <mn>2</mn>
                                 </mrow>
                              </msubsup>
                           </mrow>
                           <mrow>
                              <msqrt>
                                 <mrow>
                                    <mrow>
                                       <mo fence=true form=prefix>(</mo>
                                       <mrow>
                                          <msubsup>
                                             <mrow>
                                                <mi>Q</mi>
                                             </mrow>
                                             <mrow>
                                                <mi>t</mi>
                                                <mi>c</mi>
                                             </mrow>
                                             <mrow>
                                                <mn>2</mn>
                                             </mrow>
                                          </msubsup>
                                          <mo class=MathClass-bin>−</mo>
                                          <mn>0</mn>
                                          <mo class=MathClass-punc>.</mo>
                                          <mn>2</mn>
                                          <mn>5</mn>
                                       </mrow>
                                       <mo fence=true form=postfix>)</mo>
                                    </mrow>
                                 </mrow>
                              </msqrt>
                           </mrow>
                        </mfrac>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
                  <mrow>
                     <mo fence=true form=prefix>(</mo>
                     <mrow>
                        <mstyle mathvariant=normal>
                           <mi>d</mi>
                           <mi>B</mi>
                        </mstyle>
                     </mrow>
                     <mo fence=true form=postfix>)</mo>
                  </mrow>
               </math>
            <td class=eq-no>(14)
      </table>
      <p>
         <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/table-1.svg data-img-alt="Table 1. Ripple in closed boxes for different
            <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
            <mrow>
            <mi>Q</mi>
            </mrow>
            <mrow>
            <mi>t</mi>
            <mi>c</mi>
            </mrow>
            </msub>
            </math>
            values.">Table 1</a> shows the ripple corresponding to different
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
               <mrow>
                  <mi>Q</mi>
               </mrow>
               <mrow>
                  <mi>t</mi>
                  <mi>c</mi>
               </mrow>
            </msub>
         </math>
         values.
      <div class=table>
         <figure class=float>
            <figcaption class=caption>
               Table 1. Ripple in closed boxes for different
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
                  <msub>
                     <mrow>
                        <mi>Q</mi>
                     </mrow>
                     <mrow>
                        <mi>t</mi>
                        <mi>c</mi>
                     </mrow>
                  </msub>
               </math>
               values.
            </figcaption>
            <img src=${config.ASSETS_URL}/04/20/theory/table-1.svg alt="Table 1. Ripple in closed boxes for different
               <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
               <msub>
               <mrow>
               <mi>Q</mi>
               </mrow>
               <mrow>
               <mi>t</mi>
               <mi>c</mi>
               </mrow>
               </msub>
               </math>
               values.">
         </figure>
      </div>
   <li class=itemize>
      <strong><span class=cmbx-10>maximum achievable SPL</span></strong> <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo>
       <msub>
      <mrow>
      <mi>S</mi>
      <mi>P</mi>
      <mi>L</mi>
      </mrow>
      <mrow>
      <mi>m</mi>
      <mi>a</mi>
      <mi>x</mi>
      </mrow>
    </msub>
       <mo>)</mo>
      </math> without exceeding neither the maximal electrical power (to prevent failure) nor the maximum cone displacement
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
      <mi>X</mi>
      <mrow>
      <mstyle mathvariant=normal>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
      </mstyle>
      </mrow>
      </msub>
      </math>
      (to avoid audible distortion). Generally,
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
      <mi>X</mi>
      <mrow>
      <mstyle mathvariant=normal>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
      </mstyle>
      </mrow>
      </msub>
      </math>
      is reached before the instantaneous maximum power allowed, so the most limiting factor is, in practice, the non linear behavior of the driver.
      <p>
         Cone displacement is an important large signal question to be addressed when designing loudspeaker systems, since exceeding the maximum linear displacement of the driver is a major cause of distortion. For a given mechanical force, the extra stiffness of the air contained in the cabinet holds the diaphragm tighter to its rest position, thus offering a valuable protection against excessive cone displacement (see <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-9.svg data-img-alt="Figure 9. Cone displacement at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> W for the SEAS L26RFX/P driver in sealed enclosures with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi></math>= 0, 2, 5, 10 and 20 and considering <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>a</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>5</mn></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-rel>→</mo><mi>∞</mi></math>.">Figure 9</a>). This has a direct implication on
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
         <mrow>
         <mi>S</mi>
         <mi>P</mi>
         <mi>L</mi>
         </mrow>
         <mrow>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
         </mrow>
       </msub>
         </math>, as can be inferred from the following reasoning: for a given input signal, as
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>α</mi>
         </math>
         increases, the cone displacement decreases. This leaves more room for the input voltage to be increased until
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
         <mi>X</mi>
         <mrow>
         <mstyle mathvariant=normal>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
         </mstyle>
         </mrow>
         </msub>
         </math>
         is reached and, consequently, the input electrical power
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <msub>
               <mrow>
                  <mi>P</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
         </math>
         is also increased. As the efficiency does not depend on
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mi>α</mi>
         </math>, the
         <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
            <mstyle mathvariant=normal>
            <msub>
            <mrow>
            <mi>S</mi>
            <mi>P</mi>
            <mi>L</mi>
            </mrow>
            <mrow>
            <mi>m</mi>
            <mi>a</mi>
            <mi>x</mi>
            </mrow>
          </msub>
            </mstyle>
         </math>
         increases as well.
   </li>
</ul>
<figure class=figure>
   <img src=${config.ASSETS_URL}/04/20/theory/figure-9.svg alt="Figure 9. Cone displacement at <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mn>1</mn></math> W for the SEAS L26RFX/P driver in sealed enclosures with <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi></math>= 0, 2, 5, 10 and 20 and considering <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>a</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>5</mn></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-rel>→</mo><mi>∞</mi></math>.">
   <figcaption class=caption>
      Figure 9. Cone displacement at
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mn>1</mn>
      </math>
      W for the SEAS L26RFX/P driver in sealed enclosures with
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <mi>α</mi>
      </math>
      = 0, 2, 5, 10 and 20 and considering
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>a</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>=</mo>
         <mn>5</mn>
      </math>
      and
      <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
         <msub>
            <mrow>
               <mi>Q</mi>
            </mrow>
            <mrow>
               <mi>l</mi>
            </mrow>
         </msub>
         <mo class=MathClass-rel>→</mo>
         <mi>∞</mi>
      </math>.
   </figcaption>
</figure>
<h3 class=sectionHead>Example of closed-box design</h3>
<p>
   <a href=# data-img-src=${config.ASSETS_URL}/04/20/theory/figure-10.svg data-img-alt="Figure 10. Performance evaluation of closed-box designs for the SEAS L26RFX/P driver as function of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi></math> and considering <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>a</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>5</mn></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-rel>→</mo><mi>∞</mi></math>.">Figure 10</a> illustrates graphically how the performance parameters of closed boxes vary with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>. We employed the SEAS L26RFX/P to obtain these curves. As can be observed,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>c</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
   </math>
   rise with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>. The minimum reachable
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
   </math>
   is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mn>5</mn>
   </math>
   Hz. However, to obtain such a low
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
   </math>, large box volumes (around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math>
   l) are required. In addition, the low stiffness provided by large boxes allows for large diaphragm excursions, even with low input power. As a result, low SPL values are achievable to keep excursions below the limit imposed by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
      <mi>X</mi>
      <mrow>
      <mstyle mathvariant=normal>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
      </mstyle>
      </mrow>
      </msub>
   </math>
   (for this driver
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
      <mi>X</mi>
      <mrow>
      <mstyle mathvariant=normal>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
      </mstyle>
      </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>7</mn>
   </math>
   mm). Consequently, increasing the value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   is rather beneficial, since both
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msub>
      <mrow>
      <mi>S</mi>
      <mi>P</mi>
      <mi>L</mi>
      </mrow>
      <mrow>
      <mi>m</mi>
      <mi>a</mi>
      <mi>x</mi>
      </mrow>
    </msub>
   </math>
   improve substantially.
<p>
   The question now is at which extent
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
   </math>
   could be reasonably increased. The answer lays in which
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
   </math>
   and ripple values are acceptable. For example, let us now consider that we would accept
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≤</mo>
      <mn>5</mn>
      <mn>0</mn>
   </math>
   Hz and a ripple below
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>5</mn>
   </math>
   dB. The condition for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
   </math>
   is satisfied for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
      <mn>3</mn>
   </math>, whereas the condition for the ripple is satisfied for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>α</mi>
      <mo class=MathClass-rel><</mo>
      <mn>1</mn>
      <mn>7</mn>
   </math>
   . The most restrictive of these values <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mo>(</mo><mi>α</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mn>3</mn>
       <mo>)</mo>
   </math> should be considered as the upper limit. With this configuration, the following performance parameters are obtained:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>5</mn>
      <mn>0</mn>
   </math>
   Hz, ripple =
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>9</mn>
   </math>
   dB,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>V</mi>
         </mrow>
         <mrow>
            <mi>b</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mn>1</mn>
   </math>
   l and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle mathvariant=normal>
         <mi>S</mi>
         <mi>P</mi>
         <mi>L</mi>
      </mstyle>
      <mstyle mathvariant=normal>
         <mi>m</mi>
         <mi>a</mi>
         <mi>x</mi>
      </mstyle>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mn>0</mn>
      <mn>9</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>7</mn>
   </math>
   dB.
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/20/theory/figure-10.svg alt="Figure 10. Performance evaluation of closed-box designs for the SEAS L26RFX/P driver as function of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi></math> and considering <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>a</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>5</mn></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-rel>→</mo><mi>∞</mi></math>."><figcaption class=caption>Figure 10. Performance evaluation of closed-box designs for the SEAS L26RFX/P driver as function of <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><mi>α</mi></math> and considering <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>a</mi></mrow></msub><mo class=MathClass-rel>=</mo><mn>5</mn></math> and <math xmlns=http://www.w3.org/1998/Math/MathML display=inline><msub><mrow><mi>Q</mi></mrow><mrow><mi>l</mi></mrow></msub><mo class=MathClass-rel>→</mo><mi>∞</mi></math>.</figcaption></figure>
<h3 class=sectionHead>Conclusion</h3>
<p>In this Lesson, a model for predicting the acoustic response of a closed-box system has been derived. It has been shown that the enclosure’s stiffness increases both the resonance frequency and the quality factors. The expected effects of the filling material have also been explained.
<h3 class=likesectionHead>Glossary</h3>
<dl>
   <dt>SPL<sub>max</sup>
   <dd>Maximum achievable SPL
   <dt>IEC
   <dd>International Electrotechnical Commission
   <dt>RLC
   <dd>Resistor, Inductor, Capacitor
   <dt>SPL
   <dd>Sound Pressure Level
   <dt>T&S
   <dd>Thiele & Small
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt id=X0-small1972closed class=thebibliography>[1]
   <dd id=bib-1 class=thebibliography>
      <p>Richard H Small. “Closed-box loudspeaker systems-part 1: Analysis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>20.10 (1972), pp. 798–808.
   <dt id=X0-small1973closed class=thebibliography>[2]
   <dd id=bib-2 class=thebibliography>
      <p>Richard H Small. “Closed-box loudspeaker systems-part 2: Synthesis”. In: <span class=cmti-10>Journal of the Audio Engineering Society </span>21.1 (1973), pp. 11–18.
</dl>`;

export default theory;
